import type { GameOverDetail } from '../game/events';
import { resolveQuerySelector, setText, type QuerySelector } from './dom';

export interface ContinueDialogDeps {
  readonly reducedMotionQuery: MediaQueryList;
  readonly onContinue: () => void;
  readonly onRestart: () => void;
  readonly querySelector?: QuerySelector;
}

export interface ContinueDialogApi {
  show(detail: GameOverDetail): void;
  setError(text: string): void;
  clear(): void;
  focusHeading(): void;
  isHeadingFocused(): boolean;
  destroy(): void;
}

export function createContinueDialog(deps: ContinueDialogDeps): ContinueDialogApi {
  const querySelector = resolveQuerySelector(deps.querySelector);
  const continueHeading = querySelector<HTMLElement>('#continue-heading');
  const continueRemaining = querySelector<HTMLElement>('#continue-remaining');
  const continueGauge = querySelector<HTMLElement>('.approach-gauge');
  const continueGaugeArc = querySelector<SVGCircleElement>('.approach-gauge__progress');
  const continueCount = querySelector<HTMLElement>('#continue-count');
  const continueCountLabel = querySelector<HTMLElement>('#continue-count-label');
  const continueError = querySelector<HTMLElement>('#continue-error');
  const continueAdButton = querySelector<HTMLButtonElement>('#continue-ad-button');
  const continueRestartButton = querySelector<HTMLButtonElement>('#continue-restart-button');

  /** index.html のリング半径 50 に対応する円周（2πr）。 */
  const gaugeCircumference = 314.159;
  /** ティールの弧と琥珀の弧がまるい端で重ならないための間隔。 */
  const gaugeArcGap = 6;
  let gaugeCountFrom = 0;
  let gaugeCountTo = 0;
  let gaugeCountFrame = 0;

  /**
   * 「全 N 本のうちここまで送り出した」を進捗リングに、残り本数を中央の数字に割り当てる。
   * 実際の掃引と点灯は CSS 側のアニメーションが担当し、ここは終端値だけを渡す。
   */
  const renderContinueGauge = (remaining: number, total: number): void => {
    const safeTotal = Math.max(total, remaining, 1);
    const cleared = Math.max(0, safeTotal - remaining);
    const clearedLength = gaugeCircumference * (cleared / safeTotal);
    const remainingLength = Math.max(
      0,
      gaugeCircumference - clearedLength - (cleared === 0 ? 0 : gaugeArcGap * 2),
    );
    const remainingStart = clearedLength + (cleared === 0 ? 0 : gaugeArcGap);

    continueGauge?.style.setProperty(
      '--gauge-progress-offset',
      String(gaugeCircumference - clearedLength),
    );
    continueGauge?.style.setProperty('--gauge-remain-dash', String(remainingLength));
    continueGauge?.style.setProperty('--gauge-remain-offset', String(-remainingStart));

    gaugeCountFrom = safeTotal;
    gaugeCountTo = remaining;
    setText(continueCount, String(remaining));
    setText(continueCountLabel, `のこり ${remaining} 本`);
  };

  /** リングの掃引に合わせて、中央の数字を全本数から残り本数へ落とす。 */
  const runContinueCountdown = (): void => {
    window.cancelAnimationFrame(gaugeCountFrame);
    if (deps.reducedMotionQuery.matches || gaugeCountFrom === gaugeCountTo) {
      setText(continueCount, String(gaugeCountTo));
      return;
    }
    const duration = 760;
    const startedAt = performance.now();
    const step = (now: number): void => {
      const progress = Math.min(1, (now - startedAt) / duration);
      const eased = 1 - (1 - progress) ** 3;
      setText(
        continueCount,
        String(Math.round(gaugeCountFrom + (gaugeCountTo - gaugeCountFrom) * eased)),
      );
      if (progress < 1) {
        gaugeCountFrame = window.requestAnimationFrame(step);
      }
    };
    setText(continueCount, String(gaugeCountFrom));
    gaugeCountFrame = window.requestAnimationFrame(step);
  };

  // ダイアログが表示されるたびに CSS の掃引が走り直すので、それに同期させる。
  continueGaugeArc?.addEventListener('animationstart', runContinueCountdown);
  continueAdButton?.addEventListener('click', deps.onContinue);
  continueRestartButton?.addEventListener('click', deps.onRestart);

  const setError = (text: string): void => setText(continueError, text);

  return {
    show: (detail) => {
      const cleared = Math.max(0, detail.total - detail.remaining);
      renderContinueGauge(detail.remaining, detail.total);
      // 1 本も送り出せていないときに「あとすこし」と言うと嘘になる。
      setText(continueHeading, cleared === 0 ? 'ここから立て直そう' : '惜しい、あとすこし！');
      setText(continueRemaining, `全 ${detail.total} 本のうち ${cleared} 本を送り出しました`);
      setError('');
    },
    setError,
    clear: () => setError(''),
    focusHeading: () => continueHeading?.focus({ preventScroll: true }),
    isHeadingFocused: () => document.activeElement === continueHeading,
    destroy: () => {
      window.cancelAnimationFrame(gaugeCountFrame);
      continueGaugeArc?.removeEventListener('animationstart', runContinueCountdown);
      continueAdButton?.removeEventListener('click', deps.onContinue);
      continueRestartButton?.removeEventListener('click', deps.onRestart);
    },
  };
}
